import { createQueryKeyStore } from '@lukemorales/query-key-factory'
import {
  GetAlertsQueryVariables,
  GetCampaignOrdersQueryVariables,
  GetCoachingBadgesQueryVariables,
  GetContactRequestByTokenQueryVariables,
  GetDraftedCardsQueryVariables,
  GetItemQueryVariables,
  GetItemsQueryVariables,
  GetLayoutQueryVariables,
  GetLayoutsQueryVariables,
  GetNotificationsQueryVariables,
  GetOrderQueryVariables,
  GetPaginatedContactRequestsQueryVariables,
  GetPaginatedInvitesQueryVariables,
  GetPaginatedRecipientsQueryVariables,
  GetPlanMoveQueryVariables,
  GetRemindersQueryVariables,
  GetSentCardsCountQueryVariables,
  GetSentCardsQueryVariables,
  GetSharedCampaignsQueryVariables,
  GetSharedContactsQueryVariables,
  GetSponsorQueryVariables,
  GetStickerCategoriesQueryVariables,
  GetStickersQueryVariables,
  GetUsersQueryVariables,
} from 'src/graphql/generated/graphql'
import * as graphql from 'src/graphql/generated/graphql'
import {
  CampaignStoreSubcategoriesQueryVariables,
  CampaignStoreSubcategoryQueryVariables,
  CheckoutQueryVariables,
  ProductVariantQueryVariables,
  ProductVariantsQueryVariables,
  SaleorOrderQueryVariables,
} from 'src/saleor_graphql/generated/graphql'
import {
  getAccount,
  getAccounCreatedContents,
  getAffiliateCountries,
  getAlerts,
  getByDesignLoginLink,
  getCampaignOrders,
  getCampaignStoreSubcategories,
  getCampaignStoreSubcategory,
  getCheckout,
  getCoachingBadges,
  getCoachingDashboardMarketingContent,
  getContactRequestByToken,
  getContactRequests,
  getDetailedCountries,
  getDraftedCards,
  getFlags,
  getFonts,
  getItem,
  getItemCategories,
  getItems,
  getLayout,
  getLayouts,
  getLeaderBoardMarketingContent,
  getLeaderBoards,
  getMarketingBanners,
  getMarketingContent,
  getNotificationCount,
  getNotifications,
  getOrder,
  getPaginatedInvites,
  getPaginatedRecipients,
  getPlanMove,
  getPlans,
  getProductVariant,
  getProductVariants,
  getPromptingsCoachDiscounts,
  getPromptingsEventDates,
  getReceivedPendingMembershipInvites,
  getReminders,
  getSaleorCustomer,
  getSaleorOrder,
  getSentCards,
  getSentCardsCount,
  getSharedCampaigns,
  getSharedContacts,
  getShouldVerifyEmail,
  getStickerCategories,
  getStickers,
  getUsers,
  getExclusiveOffer,
} from './queries/fetching'
import * as fetching from './queries/fetching'

export const queryKeyStore = createQueryKeyStore({
  account: {
    detail: {
      queryFn: () => getAccount(),
      queryKey: null,
    },
  },
  countries: {
    list: {
      queryFn: () => getDetailedCountries(),
      queryKey: null,
    },
  },
  affiliate_countries: {
    list: {
      queryFn: () => getAffiliateCountries(),
      queryKey: null,
    },
  },
  daily_cap: {
    heartfelt: {
      queryFn: fetching.getAvailableDailyHeartfelt,
      queryKey: null,
    },
    unlimited_heartfelt: {
      queryFn: fetching.getAvailableUnlimitedDailyHeartfelt,
      queryKey: null,
    },
    add_payment_settings: {
      queryFn: fetching.getAddPaymentSettingsDailyCap,
      queryKey: null,
    },
  },
  deploymentInfo: {
    detail: {
      queryFn: fetching.getDeploymentInfo,
      queryKey: null,
    },
  },
  bydesign_login_link: {
    detail: {
      queryFn: () => getByDesignLoginLink(),
      queryKey: null,
    },
  },
  should_verify_email: {
    detail: {
      queryFn: () => getShouldVerifyEmail(),
      queryKey: null,
    },
  },
  alerts: {
    infinite: (variables: GetAlertsQueryVariables) => ({
      queryFn: () => getAlerts(variables),
      queryKey: [
        {
          filters: {
            reminders: variables.reminderFilters,
            notifications: variables.notificationFilters,
          },
        },
      ],
    }),
  },
  card: {
    detail: (variables: graphql.GetCardQueryVariables) => ({
      queryFn: () => fetching.getCard({ id: variables.id }),
      queryKey: [variables.id],
    }),
  },
  cardCatalog: {
    detail: {
      queryFn: () => fetching.getCardCatalog(),
      queryKey: null,
    },
  },
  cardCategory: {
    list: {
      queryFn: () => fetching.getCardCategories(),
      queryKey: null,
    },
    detail: (variables: graphql.GetCardCategoryQueryVariables) => ({
      queryFn: () => fetching.getCardCategory(variables),
      queryKey: [variables.id],
    }),
  },
  customCard: {
    infinite: (
      variables: Omit<graphql.GetCustomCardsQueryVariables, 'offset'>,
    ) => ({
      queryKey: [variables],
      queryFn: ({ pageParam: offset }) =>
        fetching.getCustomCards({
          ...variables,
          offset: offset ?? 0,
        }),
    }),
  },
  digitalCard: {
    byToken: (variables: graphql.GetDigitalCardByTokenQueryVariables) => ({
      queryFn: () => fetching.getDigitalCardByToken({ token: variables.token }),
      queryKey: [variables.token],
    }),
    infinite: (
      variables: Omit<graphql.GetDigitalCardsQueryVariables, 'after'>,
    ) => ({
      queryFn: ({ pageParam: after }) =>
        fetching.getDigitalCards({ ...variables, after }),
      queryKey: [variables],
    }),
    infiniteReceived: (
      variables: Omit<graphql.GetDigitalCardsReceivedQueryVariables, 'after'>,
    ) => ({
      queryFn: ({ pageParam: after }) =>
        fetching.getDigitalCardsReceived({ ...variables }),
      queryKey: [variables],
    }),
  },
  defaultSubscriptionIds: {
    detail: {
      queryFn: fetching.getDefaultSubscriptionIds,
      queryKey: null,
    },
  },
  legacyPicturePlusCard: {
    infinite: (
      variables: Omit<
        graphql.GetLegacyPicturePlusCardsQueryVariables,
        'offset'
      >,
    ) => ({
      queryKey: [variables],
      queryFn: ({ pageParam: offset }) =>
        fetching.getLegacyPicturePlusCards({
          ...variables,
          offset: offset ?? 0,
        }),
    }),
  },
  sendableCard: {
    detail: (variables: graphql.GetSendableCardQueryVariables) => ({
      queryFn: () => fetching.getSendableCard({ id: variables.id }),
      queryKey: [variables.id],
    }),
    infinite: (
      variables: Omit<graphql.GetSendableCardsQueryVariables, 'offset'>,
    ) => ({
      queryKey: [variables],
      queryFn: ({ pageParam: offset }) =>
        fetching.getSendableCards({ ...variables, offset: offset ?? 0 }),
    }),
    designColorsById: (
      variables: graphql.GetDesignColorsBySendableCardIdQueryVariables,
    ) => ({
      queryFn: () =>
        fetching.getGetDesignColorsBySendableCardID({ id: variables.id }),
      queryKey: [{ filters: variables }],
    }),
  },
  sent_card: {
    list_paginated: (variables: GetSentCardsQueryVariables) => ({
      queryFn: () => getSentCards(variables),
      queryKey: [{ filters: variables }],
    }),
    count: (variables: GetSentCardsCountQueryVariables) => ({
      queryFn: () => getSentCardsCount(variables),
      queryKey: [{ filters: variables }],
    }),
  },
  draftedCard: {
    list: (variables: GetDraftedCardsQueryVariables) => ({
      queryFn: () => getDraftedCards(variables),
      queryKey: [{ filters: variables }],
    }),
    infinite: (variables: Omit<graphql.GetImagesQueryVariables, 'offset'>) => ({
      queryFn: ({ pageParam: offset = 0 }) =>
        fetching.getDraftedCards({ ...variables, offset }),
      queryKey: [variables],
    }),
  },
  itemCategory: {
    list: {
      queryFn: () => getItemCategories(),
      queryKey: null,
    },
  },
  item: {
    list: (variables: GetItemsQueryVariables) => ({
      queryFn: () => getItems(variables),
      queryKey: [
        {
          filters: {
            categories: variables?.category,
            search: variables?.search,
          },
        },
      ],
    }),
    detail: (variables: GetItemQueryVariables) => ({
      queryFn: () => getItem(variables),
      queryKey: [variables],
    }),
  },
  leader_board: {
    list: {
      queryFn: () => getLeaderBoards(),
      queryKey: null,
    },
  },
  campaign: {
    detail: (variables: graphql.GetCampaignQueryVariables) => ({
      queryFn: () => fetching.getCampaign(variables),
      queryKey: [{ filters: variables }],
    }),
    listPaginated: (
      variables: graphql.GetPaginatedCampaignsQueryVariables,
    ) => ({
      queryFn: () => fetching.getPaginatedCampaigns(variables),
      queryKey: [{ filters: variables }],
    }),
  },
  upsell: {
    list: {
      queryFn: () => fetching.getUpsells(),
      queryKey: null,
    },
  },
  infinite_campaign_order: {
    list: (variables: GetCampaignOrdersQueryVariables) => ({
      queryFn: () => getCampaignOrders(variables),
      queryKey: [variables.id],
    }),
  },
  paginated_invites: {
    list: (variables: GetPaginatedInvitesQueryVariables) => ({
      queryFn: () => getPaginatedInvites(variables),
      queryKey: [{ filters: variables }],
    }),
    infinite: (variables: GetPaginatedInvitesQueryVariables) => ({
      queryFn: () => getPaginatedInvites(variables),
      queryKey: [''],
    }),
  },
  production_info: {
    detail: (variables: graphql.GetProductionInfoQueryVariables) => ({
      queryFn: () => fetching.getProductionInfo(variables),
      queryKey: [variables],
    }),
  },
  recipients: {
    list: (variables: GetPaginatedRecipientsQueryVariables) => ({
      queryFn: () => getPaginatedRecipients(variables),
      queryKey: [{ filters: variables }],
    }),
  },
  flags: {
    list: {
      queryFn: () => getFlags(),
      queryKey: null,
    },
  },
  marketing_content: {
    list: {
      queryFn: () => getMarketingContent(),
      queryKey: null,
    },
    affiliate_join: {
      queryFn: fetching.getAffiliateJoinMarketingContent,
      queryKey: null,
    },
    leader_board: {
      queryFn: () => getLeaderBoardMarketingContent(),
      queryKey: null,
    },
    coaching_dashboard: {
      queryFn: () => getCoachingDashboardMarketingContent(),
      queryKey: null,
    },
    credit_card_manager: {
      queryFn: fetching.getCreditCardManagerMarketingContent,
      queryKey: null,
    },
    contact_request_mkt: {
      queryFn: fetching.getContactRequesMarketingContent,
      queryKey: null,
    },
    submit_requested_contact: {
      queryFn: fetching.getSubmitRequestedContactMarketingContent,
      queryKey: null,
    },
    promptings_event_dates: {
      queryFn: () => getPromptingsEventDates(),
      queryKey: null,
    },
    gratitude_day: {
      queryFn: fetching.getGratitudeDayMarketingContent,
      queryKey: null,
    },
    account_created: {
      queryFn: () => getAccounCreatedContents(),
      queryKey: null,
    },
    act_recipient_action_screens: {
      queryFn: fetching.getActRecipientActionScreenContent,
      queryKey: null,
    },
  },
  marketing_banner: {
    list: {
      queryFn: () => getMarketingBanners(),
      queryKey: null,
    },
  },
  layout: {
    list: (variables: GetLayoutsQueryVariables) => ({
      queryFn: () => getLayouts(variables),
      queryKey: [{ filters: variables }],
    }),
    infinite: (variables: GetLayoutsQueryVariables) => ({
      queryFn: () => getLayouts(variables),
      queryKey: [{ filters: variables }],
    }),
    detail: (variables: GetLayoutQueryVariables) => ({
      queryFn: () => getLayout(variables),
      queryKey: [variables.id],
    }),
  },
  image: {
    list: (variables: graphql.GetImagesQueryVariables) => ({
      queryFn: () => fetching.getImages(variables),
      queryKey: [{ filters: variables }],
    }),
    infinite: (variables: Omit<graphql.GetImagesQueryVariables, 'offset'>) => ({
      queryFn: ({ pageParam: offset = 0 }) =>
        fetching.getImages({ ...variables, offset }),
      queryKey: [variables],
    }),
  },
  referralCredits: {
    balance: {
      queryFn: () => fetching.getCreditsBalance(),
      queryKey: null,
    },
    detailed: {
      queryFn: () => fetching.getDetailedReferralCredits(),
      queryKey: null,
    },
  },
  digital_card_order: {
    detail: (variables: graphql.GetDigitalCardQueryVariables) => ({
      queryFn: () => fetching.getDigitalCardOrder(variables),
      queryKey: [variables.id],
    }),
  },
  order: {
    detail: (variables: GetOrderQueryVariables) => ({
      queryFn: () => getOrder(variables),
      queryKey: [variables.id],
    }),
  },
  font: {
    list: {
      queryFn: () => getFonts(),
      queryKey: null,
    },
  },
  coaching_badges: {
    list: (variables: GetCoachingBadgesQueryVariables) => ({
      queryFn: () => getCoachingBadges(variables),
      queryKey: [{ filters: variables }],
    }),
  },
  plan: {
    list: {
      queryFn: () => getPlans(),
      queryKey: null,
    },
  },
  plan_move: {
    detail: (variables: GetPlanMoveQueryVariables) => ({
      queryFn: () => getPlanMove(variables),
      queryKey: [{ filters: variables }],
    }),
  },
  promptings_coach_discounts: {
    detail: {
      queryFn: () => getPromptingsCoachDiscounts(),
      queryKey: null,
    },
  },
  notification: {
    list: (variables: GetNotificationsQueryVariables) => ({
      queryFn: () => getNotifications(variables),
      queryKey: [{ filters: variables }],
    }),
    count: {
      queryFn: () => getNotificationCount(),
      queryKey: null,
    },
  },
  received_pending_membership_invites: {
    detail: {
      queryFn: () => getReceivedPendingMembershipInvites(),
      queryKey: null,
    },
  },
  reminders: {
    detail: (variables: GetRemindersQueryVariables) => ({
      queryFn: () => getReminders(variables),
      queryKey: [{ filters: variables }],
    }),
  },
  shared_campaigns: {
    detail: (variables: GetSharedCampaignsQueryVariables) => ({
      queryFn: () => getSharedCampaigns(variables),
      queryKey: [{ filters: variables }],
    }),
  },
  shared_contacts: {
    detail: (variables: GetSharedContactsQueryVariables) => ({
      queryFn: () => getSharedContacts(variables),
      queryKey: [{ filters: variables }],
    }),
  },
  contact_requests: {
    list: {
      queryFn: () => getContactRequests(),
      queryKey: null,
    },
    by_token: (variables: GetContactRequestByTokenQueryVariables) => ({
      queryFn: () => getContactRequestByToken(variables),
      queryKey: [variables.token],
    }),
    list_paginated: (variables: GetPaginatedContactRequestsQueryVariables) => ({
      queryFn: () => fetching.getPaginatedContactRequests(variables),
      queryKey: [{ filters: variables }],
    }),
  },
  checkout: {
    detail: (variables: CheckoutQueryVariables) => ({
      queryFn: () => getCheckout(variables),
      queryKey: [variables.id],
    }),
  },
  saleor_customer: {
    detail: {
      queryKey: null,
      queryFn: () => getSaleorCustomer(),
    },
  },
  saleor_product_variant: {
    detail: (variables: ProductVariantQueryVariables) => ({
      queryFn: () => getProductVariant(variables),
      queryKey: [variables],
    }),
  },
  saleor_infinite_product_variants: {
    list: (variables: ProductVariantsQueryVariables) => ({
      queryFn: () => getProductVariants(variables),
      queryKey: [{ filters: variables.filter, ids: variables.ids }],
    }),
  },
  saleor_order: {
    detail: (variables: SaleorOrderQueryVariables) => ({
      queryFn: () => getSaleorOrder(variables),
      queryKey: [variables.id],
    }),
  },
  sticker_category: {
    infinite: (variables: GetStickerCategoriesQueryVariables) => ({
      queryFn: () => getStickerCategories(variables),
      queryKey: ['new_editor_sticker_categories'],
    }),
  },
  infinite_campaign_store_subcategories: {
    list: (variables: CampaignStoreSubcategoriesQueryVariables) => ({
      queryFn: () => getCampaignStoreSubcategories(variables),
      queryKey: [variables],
    }),
  },
  infinite_campaign_store_subcategory_products: {
    list: (variables: CampaignStoreSubcategoryQueryVariables) => ({
      queryFn: () => getCampaignStoreSubcategory(variables),
      queryKey: [variables],
    }),
  },
  lines: {
    list: (filters: { checkoutId: string }) => ({
      queryFn: () => getCheckout({ id: filters.checkoutId }),
      queryKey: [{ filters }],
    }),
  },
  infinite_stickers: {
    list: (variables: GetStickersQueryVariables) => ({
      queryFn: () => getStickers(variables),
      queryKey: ['new_editor_stickers', { filters: variables.filters }],
    }),
  },
  subscription: {
    list: {
      queryFn: () => fetching.getSubscriptions({}),
      queryKey: null,
    },
  },
  sponsor: {
    detail: (variables: GetSponsorQueryVariables) => ({
      queryFn: () => fetching.getSponsor(variables),
      queryKey: [variables],
    }),
  },
  user: {
    list: (variables: GetUsersQueryVariables) => ({
      queryFn: () => getUsers(variables),
      queryKey: [{ filters: variables }],
    }),
  },
  contact: {
    detail: (variables: graphql.GetContactQueryVariables) => ({
      queryFn: () => fetching.getContact(variables),
      queryKey: [{ filters: variables }],
    }),
    list: (variables: graphql.GetContactsQueryVariables) => ({
      queryFn: () => fetching.getContacts(variables),
      queryKey: [{ filters: variables }],
    }),
    listMinimal: (variables: graphql.GetMinimalContactsQueryVariables) => ({
      queryFn: () => fetching.getMinimalContacts(variables),
      queryKey: [{ filters: variables }],
    }),
    listPaginated: (variables: graphql.GetPaginatedContactsQueryVariables) => ({
      queryFn: () => fetching.getPaginatedContacts(variables),
      queryKey: [{ filters: variables }],
    }),
    listIds: (variables: graphql.GetContactIdsQueryVariables) => ({
      queryFn: () => fetching.getContactIds(variables),
      queryKey: [{ filters: variables }],
    }),
    infiniteMinimal: (
      variables: Omit<
        graphql.GetPaginatedMinimalContactsQueryVariables,
        'offset'
      >,
    ) => ({
      queryFn: ({ pageParam: offset = 0 }) =>
        fetching.getPaginatedMinimalContacts({ ...variables, offset }),
      queryKey: [variables],
    }),
    listChildren: (variables: graphql.GetContactChildrenQueryVariables) => ({
      queryFn: () => fetching.getContactChildren(variables),
      queryKey: [{ filters: variables }],
    }),
  },
  group: {
    detailed: (variables: graphql.GetGroupQueryVariables) => ({
      queryFn: () => fetching.getGroup(variables),
      queryKey: [{ filters: variables }],
    }),
    listPaginated: (variables: graphql.GetPaginatedGroupsQueryVariables) => ({
      queryFn: () => fetching.getPaginatedGroups(variables),
      queryKey: [{ filters: variables }],
    }),
    listAll: {
      queryFn: fetching.getAllGroups,
      queryKey: null,
    },
  },
  user_card_tokens: {
    detail: {
      queryKey: null,
      queryFn: fetching.getUserCardTokens,
    },
  },
  exclusive_offer: {
    detail: {
      queryFn: () => getExclusiveOffer(),
      queryKey: null,
    },
  },
  digital_gift_card_product: {
    detail: (variables: graphql.GetDigitalGiftCardProductQueryVariables) => ({
      queryFn: () => fetching.getDigitalGiftCardProduct(variables),
      queryKey: [variables.code],
    }),
    listPaginated: (
      variables: Omit<
        graphql.GetPaginatedDigitalGiftCardProductsQueryVariables,
        'after'
      >,
    ) => ({
      queryFn: ({ pageParam: after = null }) =>
        fetching.getPaginatedDigitalGiftCardProducts({ ...variables, after }),
      queryKey: [{ filters: variables }],
    }),
  },
  digital_gift_cards_available_countries: {
    list: {
      queryFn: () => fetching.getDigitalGiftCardsAvailableCountries(),
      queryKey: null,
    },
  },
  global_settings: {
    detail: {
      queryFn: () => fetching.getGlobalSettings(),
      queryKey: null,
    },
  },
})
